/**
 * The one mechanism every sign-in path (password, SSO, registration) uses to remember where a
 * signed-out visitor was headed, and to decide whether a return path is safe to act on.
 *
 * Lives in the URL query string on purpose, never in router state and never in browser storage:
 * SSO leaves the SPA for the provider's own site and back, which drops router state entirely, and
 * storage would leak one visitor's intended page into the next person's sign-in on a shared device.
 * The query string is read fresh on every render from the address actually being visited, so nothing
 * survives a sign-out (see RequireAuth.tsx and the sign-out call sites, which all navigate to a plain
 * "/login" with no `from`).
 */

import { isWizardPath, wizardPathFor } from '../onboarding/demoIntent';

/** Front-end page param: `?from=` on /login and /register. Distinct from the API's own `return` query
 *  param on /auth/sso/{p}/start and the `return` fragment key on /sso/callback and /sso/complete — those
 *  are pre-existing API wire names, unchanged by this file. */
export const RETURN_TO_PARAM = 'from';

/** Mirrors SsoController.SafeReturn. */
export const MAX_RETURN_PATH_LENGTH = 512;

/**
 * Same-origin hand-off pages: a return path must be a destination, never a token or a session swap.
 * SsoCallbackPage and SsoCompletePage read a bearer token straight out of the URL fragment the instant
 * they mount, ImpersonatePage does the same for a 15-minute customer session, and ActivatePage does the
 * same for the one-tap activation-mail link (`activationExchange`, straight off the fragment on mount —
 * see ActivatePage.tsx); DemoPage swaps the caller's session for the shared read-only account just as
 * unconditionally, no click and no fragment needed at all. None of the four may ever be a place someone
 * is *returned to*: today a parked `/login?from=/activate` is harmless only because ActivatePage finds no
 * token of its own and falls back to /billing on its own account — this list should not depend on that
 * page's own fallback behaviour to stay safe.
 */
const HAND_OFF_FIRST_SEGMENTS = new Set(['sso', 'impersonate', 'demo', 'activate']);

/** ASCII C0 controls and DEL. Tab/CR/LF matter most: WHATWG URL parsing deletes them wherever they
 *  occur, before resolving what is left — so "/\t/evil.example" and "/\n/evil.example" both parse as
 *  if they were "//evil.example", a different origin, even though the raw string starts with one slash
 *  and holds no backslash. */
function hasControlCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

function firstSegment(path: string): string {
  const rest = path.slice(1);
  const end = rest.search(/[/?]/);
  return (end === -1 ? rest : rest.slice(0, end)).toLowerCase();
}

/** Found on independent review: a "." or ".." path segment lets react-router's own path resolution
 *  shift what the *effective* first segment is once the value is actually navigated to — resolvePath's
 *  resolvePathname (react-router@7.18.2, chunk-62JRHF6Z.mjs) collapses dot segments exactly like a
 *  browser's own history.pushState does, so "/./demo" and "/a/../demo" both resolve to plain "/demo"
 *  even though firstSegment() (above) reads their raw first segment as "." — never seeing "demo" at
 *  all. A percent-encoded character is the same class of gap one layer down: react-router's route
 *  matching decodes each path segment before comparing it to a route pattern (matchRoutes -> decodePath),
 *  so "/%64emo" is matched against the "/demo" route even though it contains no literal "d","e","m","o"
 *  in sequence for firstSegment() to catch. Reject the whole value outright whenever either appears in
 *  the path portion, rather than trying to fully mirror either normalisation step here — the same
 *  denylist-over-parser-trust approach the hand-off check above already takes. */
function hasUnresolvedPathTrick(value: string): boolean {
  const pathOnly = value.split('?')[0];
  if (pathOnly.includes('%')) return true;
  return pathOnly.split('/').some((segment) => segment === '.' || segment === '..');
}

/**
 * Same-site, single-leading-slash, no scheme/host, no backslash trick, no fragment, no control
 * character, no dot-segment or percent-encoding trick that could shift the first path segment, not a
 * credential/session hand-off page, bounded length.
 *
 * The origin check is resolved against a base URL that exists nowhere, so a scheme or a host shows up as
 * a different origin without hand-enumerating every trick that produces one — but it is deliberately not
 * the *only* check: a fragment does not change a URL's origin in any parser (a return path of
 * "/impersonate#access=x" resolves to this same, safe origin), so the explicit checks above come first.
 */
export function isSafeReturnPath(value: string | null | undefined): value is string {
  if (!value || value.length > MAX_RETURN_PATH_LENGTH) return false;
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  // A return path is a path and a query string, never a fragment: `from`/`return` travel through the
  // API's own query string and into oauth_states/sso_tickets — never accept what cannot be stored there
  // safely, and never let a page that trusts window.location.hash on mount be a "place to land".
  if (value.includes('#') || value.includes('\\') || hasControlCharacter(value)) return false;
  if (hasUnresolvedPathTrick(value)) return false;
  if (HAND_OFF_FIRST_SEGMENTS.has(firstSegment(value))) return false;
  try {
    return new URL(value, 'https://returnto.invalid').origin === 'https://returnto.invalid';
  } catch {
    return false;
  }
}

/** The path+query RequireAuth is about to bounce away from — undefined for the default landing, so
 *  the common case keeps producing a plain "/login" instead of a noisy "/login?from=%2F". */
export function returnToFor(pathname: string, search: string): string | undefined {
  const path = `${pathname}${search}`;
  return path === '/' ? undefined : path;
}

/** `base` with `?from=` attached, only when there is a safe value worth remembering. */
export function withReturnTo(base: string, returnTo: string | undefined): string {
  return isSafeReturnPath(returnTo) ? `${base}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}` : base;
}

/** Reads `?from=` back off a query string. Undefined when absent, unsafe, or the empty default. */
export function readReturnTo(search: string): string | undefined {
  const raw = new URLSearchParams(search).get(RETURN_TO_PARAM);
  return isSafeReturnPath(raw) ? raw : undefined;
}

/** Where an already-authenticated visitor who opens /login lands (rule 7): the parked `?from=` when
 *  there is one, otherwise `fallback` (the dashboard). Deliberately no wizard-only restriction here,
 *  unlike RegisterPage's own post-submit redirect (rule 6): an already-authenticated visitor has
 *  already onboarded, so there is nothing to skip — a stale tab or a forwarded link should land
 *  exactly where signing in there would have.
 *
 *  Not used for /register any more — see `registerRedirectTarget` below, and its own doc comment for
 *  why /register can no longer share this unrestricted formula. */
export function targetForAlreadyAuthenticated(search: string, fallback: string): string {
  return readReturnTo(search) ?? fallback;
}

/**
 * The rule behind every "where does a brand-new account land" decision, whatever door that account was
 * created through: `candidate` wins only when it is the connect wizard's own path (with or without its
 * own query string — e.g. the Customer-A shape, `/devices/new?provider=enphase&connection=<id>`);
 * anything else — absent, unsafe, or safe but pointed somewhere else — falls back to the wizard
 * (`wizardPathFor`), so a return path meant for an *existing* account (the `{{portalUrl}}/billing`
 * activation-mail link, say, forwarded through "Create one") can never let a brand-new, zero-device
 * account skip onboarding or land on someone else's page.
 *
 * `registerRedirectTarget` (password registration) and `SsoCompletePage`'s own post-accept redirect (a
 * brand-new SSO account — `SsoController.Complete` calls `RegisterFromSsoAsync` unconditionally, so
 * every visitor who reaches that page is, structurally, exactly as new as one who just filled in this
 * form) each resolve their own `candidate` differently — one off the `?from=` query param, the other off
 * the SSO ticket's own `return=` fragment key — and then both call this one function, so a brand-new
 * account cannot skip onboarding by whichever door it came through.
 */
export function newAccountTarget(candidate: string | undefined, search?: string): string {
  return candidate && isWizardPath(candidate) ? candidate : wizardPathFor(search);
}

/**
 * Where /register sends a visitor once `status` becomes `'authenticated'` — whether that visitor just
 * submitted the form (RegisterPage's own post-submit redirect, rule 6) or was already signed in and
 * opened /register anyway (`RedirectIfAuthenticated`'s /register case, rule 7). A parked `?from=` wins
 * only when it targets the connect wizard's own path; anything else falls back to the wizard, exactly
 * as an absent one always has — `newAccountTarget` above is the rule; this just supplies its candidate.
 *
 * Found on review, and confirmed against the installed react-router (`BrowserRouter`,
 * chunk-62JRHF6Z.mjs:10391-10416) and react (19.2.8): `BrowserRouter` defers every location update
 * through `React.startTransition`, while `AuthProvider`'s status flip after `register()` resolves is a
 * plain, higher-priority update — so for a few milliseconds around registration, RegisterPage's own
 * `navigate()` and `RedirectIfAuthenticated`'s reactive `<Navigate>` can each compute from a *different*
 * snapshot of `location.search` (one fresh, one still showing the pre-navigate address), and whichever
 * of the two competing history writes lands last wins the address bar — independent of which one was
 * *called* first. Reordering the two calls (an earlier revision's fix for a different, narrower
 * instance of this) does not help: the deciding factor is lane priority, not call order.
 *
 * The fix is to remove the disagreement rather than race to win it: both call sites now call this one
 * function, so whichever of the two locations either of them happens to see, they compute the same
 * answer from it and the two competing history writes can no longer diverge. This is a deliberate,
 * reasoned departure from rule 7's literal "no wizard-only restriction [at /register]" for the narrow
 * case of a *genuinely* already-authenticated visitor opening /register with a non-wizard `?from=`
 * (rare — most callers reach /register only while anonymous): such a visitor now also lands on the
 * wizard instead of `from` directly, recoverable with one more click. The alternative (rule 7 exactly
 * as worded) leaves rule 6 unenforceable for every brand-new registration that carries a `from=`,
 * including the Customer-A shape this stream exists to fix — see the stream's report for the fuller
 * reasoning and the alternatives considered (a `useTransitions={false}` prop on `<BrowserRouter>`, or
 * wrapping `AuthProvider`'s status updates in `startTransition`) and rejected as outside this file's,
 * and this stream's, ownership (`main.tsx`, `AuthProvider.tsx`).
 */
export function registerRedirectTarget(search: string): string {
  return newAccountTarget(readReturnTo(search), search);
}
